import React, {useCallback, useRef} from 'react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isOperatorField} from '../../utils/filters';
import {FormInput} from '../FormInput/FormInput';
import {FormText} from '../FormText';
import {getPlaceholderByColumn} from '../../utils/placeholders';
import {getLang} from '../../../../Localization';
import {Column, Field, formsStore} from '../../../../stores/Form';
import {FormActiveElement} from '../../../../stores/Form/Form';
import {SuspendedJobTypes} from '../../consts/suspendedJobTypes';
import {Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {handleFormBooleanValue} from '../../actions/utils/handleFormBooleanValue';

type Props = {
    isActiveCell: boolean;
    isCurrentRowActive: boolean;
    column: Column;
    searchMode: number;
    rowIndex: number;
    onInputFocus: (e: FormActiveElement) => void;
    onMaxWidthMode: () => void;
    updateSearchValue: (val: string, colId: number, rowIndex: number) => void;
    currField?: Field;
    searchValue?: string;
    openSelectListModal: (lisType) => void;
    onBlur?: (e) => void;
    openRecordInFocusView?: (e) => void;
};

const lang = getLang();

export const CellContent = (props: Props) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const {
        isActiveCell,
        column,
        searchMode,
        rowIndex,
        onMaxWidthMode,
        onInputFocus,
        updateSearchValue,
        currField,
        openSelectListModal,
        searchValue,
        openRecordInFocusView,
        onBlur,
        isCurrentRowActive
    } = props;

    const fieldValue = currField?.currentValue || '';
    const origValue = currField?.origValue || '';

    const getCellValue = useCallback(() => {
        if (searchMode) {
            return searchValue;
        }
        return fieldValue || getPlaceholderByColumn(column, lang);
    }, [searchMode, searchValue, column, fieldValue]);

    const getCellWrap = useCallback(() => contentRef.current, []);

    const updateCurrValue = useCallback(
        async arg => {
            const lastElement =
                formsStore.recordingMacro?.eventArray[
                    formsStore.recordingMacro?.eventArray.length - 1
                ];
            const conditionOfType = lastElement?.type === 'click';

            if (conditionOfType) {
                formsStore.setEventForRecordingMacro({
                    type: SuspendedJobTypes.FieldValue,
                    value: arg
                });
            }

            if (column.Type === priority.netitems.form.ColumnType.Boolean) {
                await handleFormBooleanValue(column, currField);
            } else {
                currField.setCurrentValue(arg);
            }
        },
        [currField, column]
    );

    if (
        isActiveCell ||
        (column.Type === priority.netitems.form.ColumnType.Boolean &&
            !isOperatorField(!!searchMode, rowIndex))
    ) {
        const isMasterForm = column.parentForm?.level === 0;

        return (
            <div
                ref={contentRef}
                className="priority-grid__cell-content"
                data-anchor="formField"
                onBlur={onBlur}
            >
                {isMasterForm && column.index === 0 && !searchMode ? (
                    <Icon
                        style={{paddingInlineStart: 18}}
                        icon={PriorityIcon.expand}
                        onClick={openRecordInFocusView}
                    />
                ) : null}

                <FormInput
                    origValue={origValue}
                    isActive={isActiveCell}
                    initValue={searchMode ? searchValue : fieldValue}
                    column={column}
                    rowIndex={rowIndex}
                    updateCurrentValue={updateCurrValue}
                    searchMode={!!searchMode}
                    updateSearchValue={updateSearchValue}
                    onMaxWidthMode={onMaxWidthMode}
                    onFocus={onInputFocus}
                    getCellWrap={getCellWrap}
                    openSelectListModal={openSelectListModal}
                    viewMode="table"
                    themeStyle={isCurrentRowActive ? 'dark' : 'light'}
                />
            </div>
        );
    }

    return <FormText value={getCellValue() as string} column={column} />;
};
